// Typewriter worker service
// Manages the web worker for typewriter animations

interface TypewriterAnimationCallbacks {
  onWordUpdate: (currentWordIndex: number) => void;
  onComplete: () => void;
}

class TypewriterWorkerService {
  private worker: Worker | null = null;
  private currentCallbacks: TypewriterAnimationCallbacks | null = null;

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      // Create worker from the worker file
      this.worker = new Worker(
        new URL("../workers/typewriter-worker.ts", import.meta.url),
        { type: "module" }
      );

      this.worker.onmessage = (event) => {
        const { type, currentWordIndex } = event.data;

        if (this.currentCallbacks) {
          if (type === "word") {
            this.currentCallbacks.onWordUpdate(currentWordIndex);
          } else if (type === "complete") {
            this.currentCallbacks.onComplete();
            this.currentCallbacks = null;
          }
        }
      };

      this.worker.onerror = (error) => {
        console.error("Typewriter worker error:", error);
      };
    } catch (error) {
      console.error("Failed to initialize typewriter worker:", error);
      // Fallback to main thread timing if worker fails
      this.worker = null;
    }
  }

  startAnimation(
    totalWords: number,
    animationDelay: number,
    callbacks: TypewriterAnimationCallbacks,
    startIndex: number = 0
  ) {
    // Stop any existing animation
    this.stopAnimation();

    // Store callbacks
    this.currentCallbacks = callbacks;

    if (this.worker) {
      // Use web worker for timing
      this.worker.postMessage({
        type: "start",
        totalWords,
        animationDelay,
        startIndex,
      });
    } else {
      // Fallback to main thread timing
      this.startFallbackAnimation(
        totalWords,
        animationDelay,
        callbacks,
        startIndex
      );
    }
  }

  private startFallbackAnimation(
    totalWords: number,
    animationDelay: number,
    callbacks: TypewriterAnimationCallbacks,
    startIndex: number = 0
  ) {
    let currentWordIndex = startIndex;

    const animate = () => {
      currentWordIndex++;
      callbacks.onWordUpdate(currentWordIndex);

      if (currentWordIndex < totalWords) {
        setTimeout(animate, animationDelay);
      } else {
        callbacks.onComplete();
        this.currentCallbacks = null;
      }
    };

    // If we're starting from a non-zero index, we need to trigger the first update immediately
    if (startIndex > 0) {
      callbacks.onWordUpdate(startIndex);
    }

    setTimeout(animate, animationDelay);
  }

  stopAnimation() {
    if (this.worker) {
      this.worker.postMessage({
        type: "stop",
      });
    }
    this.currentCallbacks = null;
  }

  dispose() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.currentCallbacks = null;
  }
}

// Create singleton instance
export const typewriterWorkerService = new TypewriterWorkerService();
